import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {
  BackButton,
  PtsPaymentTerminal,
  PtsPaymentTerminalSearchEvent,
  SelectionItemButton,
  StartButton,
  TerminalSessionResponse,
  UiElement,
  UiLogResponse,
} from '../../../pts-payment-terminal-model';
import { MatDialog } from '@angular/material/dialog';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from 'ok-angular-shared/components';
import { PtsTerminalConnectorRelationDialogComponent } from '../../terminal-connector-relation-dialog/terminal-connector-relation-dialog.component';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'adm-ui-log-card',
  templateUrl: './ui-log-card.component.html',
  styleUrl: './ui-log-card.component.scss',
  imports: [RouterLink, CommonModule, MatTooltipModule, MatIconModule],
})
export class UiLogCardComponent implements OnInit {
  private dialog = inject(MatDialog);
  private ptsPaymentTerminalService = inject(PtsPaymentTerminalService);
  snackbarService = inject(SnackbarService);

  @Input() uiLog: UiLogResponse;
  @Input() paymentTerminal: PtsPaymentTerminal;
  @Input() showSerialNumber = false;
  @Input() showUpdated = false;
  @Output() refreshAfterUpdateEvent = new EventEmitter<TerminalSessionResponse>();

  @Output() uiTerminalLogsEvent = new EventEmitter<PtsPaymentTerminal>();
  @Output() terminalEmulatorEvent = new EventEmitter<PtsPaymentTerminal>();
  @Output() terminalConnectorRelationEvent = new EventEmitter<PtsPaymentTerminal>();

  public uiLogType: string;

  ngOnInit(): void {
    this.enrichUiElement();
  }
  enrichUiElement() {
    if (this.uiLog?.jsonUi) {
      const parsedUiLog = JSON.parse(this.uiLog.jsonUi) as UiElement;

      this.uiLog.uiElement = parsedUiLog;
      this.uiLog.uiElement.selectionItems = [];

      this.uiLogType = parsedUiLog.type ? parsedUiLog.type : 'title';

      const labels = Object.keys(parsedUiLog.properties).filter(key => key.startsWith('items.') && key.endsWith('.label'));
      const icons = Object.keys(parsedUiLog.properties).filter(key => key.startsWith('items.') && key.endsWith('.ico'));

      for (let i = 0; i < labels.length; i++) {
        const selectionItem: SelectionItemButton = {
          icon: parsedUiLog.properties[icons[i]],
          label: parsedUiLog.properties[labels[i]],
          value: labels[i],
        };
        this.uiLog.uiElement.selectionItems.push(selectionItem);
      }

      const messageString = Object.keys(parsedUiLog.properties).filter(key => key.indexOf('message') > -1);

      const bgString = Object.keys(parsedUiLog.properties).filter(key => key.indexOf('background') > -1);

      const startButton = Object.keys(parsedUiLog.properties).filter(key => key.indexOf('buttons.start.label') > -1);

      const backButton = Object.keys(parsedUiLog.properties).filter(key => {
        return key.startsWith('items.') && key.endsWith('.label') && key.indexOf('cancel') > -1;
      });

      if (messageString[0]) {
        this.uiLog.uiElement.message = parsedUiLog.properties[messageString[0]];
      }
      if (startButton[0]) {
        this.uiLog.uiElement.startButton = { label: parsedUiLog.properties[startButton[0]], value: startButton[0] } as StartButton;
      }
      if (backButton[0]) {
        this.uiLog.uiElement.backButton = { label: parsedUiLog.properties[backButton[0]], value: backButton[0] } as BackButton;
      }
      if (bgString[0]) {
        this.uiLog.uiElement.background = parsedUiLog.properties[bgString[0]];
      }
    }
  }

  public getIconAsset(assetName: string) {
    switch (assetName) {
      case 'alias':
        return 'Indtast venglist et ID';
      case 'emailAddress':
        return 'Indtast venligst en valid email adresse';
      case 'name':
        return 'Indtast venligst navn';
      case 'selectedRole':
        return 'Vælg venligst en rolle';
      default:
        return '';
    }
  }

  public async terminalUiLogs(serialNumber: string) {
    const paymentTerminalSearchEvent: PtsPaymentTerminalSearchEvent = new PtsPaymentTerminalSearchEvent();
    paymentTerminalSearchEvent.query = serialNumber;
    const paymentTerminal = (await firstValueFrom(this.ptsPaymentTerminalService.getPaymentTerminals(paymentTerminalSearchEvent)))
      ?.items[0];

    if (paymentTerminal) {
      this.uiTerminalLogsEvent.emit(paymentTerminal);
    } else {
      this.snackbarService.error('Der kunne ikke findes nogen terminal med serie nummer: ' + serialNumber);
    }
  }

  public async terminalEmulator(serialNumber: string) {
    const paymentTerminalSearchEvent: PtsPaymentTerminalSearchEvent = new PtsPaymentTerminalSearchEvent();
    paymentTerminalSearchEvent.query = serialNumber;
    const paymentTerminal = (await firstValueFrom(this.ptsPaymentTerminalService.getPaymentTerminals(paymentTerminalSearchEvent)))
      ?.items[0];

    if (paymentTerminal) {
      //SOHY: 2024-10-15 - Hack for a hotfix. This will be removed in the future.
      const payterTerminal = await firstValueFrom(this.ptsPaymentTerminalService.getPayterTerminalBySerialNumber(serialNumber));

      if (payterTerminal) {
        paymentTerminal.name = payterTerminal.terminalName;
        paymentTerminal.online = payterTerminal.online;
      }

      this.terminalEmulatorEvent.emit(paymentTerminal);
    } else {
      this.snackbarService.error('Der kunne ikke findes nogen terminal med serie nummer: ' + serialNumber);
    }
  }

  public async terminalConnectorRelation(serialNumber: string) {
    const ptsPaymentTerminalSearchEvent = new PtsPaymentTerminalSearchEvent();
    ptsPaymentTerminalSearchEvent.query = serialNumber;
    const paymentTerminal = await firstValueFrom(this.ptsPaymentTerminalService.getPaymentTerminals(ptsPaymentTerminalSearchEvent));
    const dialogRef = this.dialog.open(PtsTerminalConnectorRelationDialogComponent, {
      data: {
        paymentTerminal: paymentTerminal.items[0],
      },
      width: '760px',
      minHeight: '420px',
    });

    dialogRef.afterClosed().subscribe((paymentTerminal: PtsPaymentTerminal) => {
      if (paymentTerminal) {
        this.refreshAfterUpdateEvent.emit();
      }
    });
  }
}
